import { patchState, signalStore, type, withHooks, withMethods } from '@ngrx/signals';
import { entityConfig, removeEntities, setEntities, withEntities } from '@ngrx/signals/entities';
import { IrisFolderPermissionsService } from '../services/folder-permissions.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, mergeMap, of, pipe } from 'rxjs';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { FolderPermissionActionType } from '../models/IrisDMSFolderPermission';
import { IrisDmsRecentFilesFolder } from '../models/IrisDMSFolder';

const actionTypes: FolderPermissionActionType[] = Object.keys(FolderPermissionActionType).map(key => FolderPermissionActionType[key]);
export const selectId = (entity: Partial<IrisDmsUserPermissionI>): string => `${entity.folderId}_${entity.action}`;

export interface IrisDmsUserPermissionI {
  action: string;
  allowed: boolean;
  folderId: string;
  loading: boolean;
}

const config = entityConfig({
  entity: type<IrisDmsUserPermissionI>(),
  selectId,
});

export const IrisDmsUserPermissionsStore = signalStore(
  { providedIn: 'root' },
  withEntities(config),
  withMethods(store => ({
    getUserPermissionsByFolderIdAndAction: (folderId: string, action: FolderPermissionActionType) => computed(() => {
      return !!store.entityMap()[selectId({ folderId, action })]?.allowed;
    }),
    getUserAvailableActions: (folderId: string) => computed(() => {
      return store.entities().filter(entity => entity.folderId === folderId);
    }),
  })),
  withMethods((
    store,
  ) => ({
    setFolderLoagingState: (folderId: string, loading: boolean) => {
      const permissions = actionTypes.map(action => {
        const allowed = store.getUserPermissionsByFolderIdAndAction(folderId, action)();
        return { folderId, action, allowed, loading };
      });
      patchState(store, setEntities(permissions, config));
    },
    isFolderLoading: (folderId: string) => store.entities().some(entity => entity.folderId == folderId && entity.loading),
    resetPermissions: (folderId: string) => {
      patchState(store, removeEntities(entity => entity.folderId === folderId));
    },
    mapActionsToPermissions: (folderId: string, actions: FolderPermissionActionType[] = actionTypes) => {
      return actionTypes.map(action => ({ folderId, action, allowed: (actions || []).includes(action), loading: false }));
    },
  })),
  withMethods((
    store,
    folderPermissionsService = inject(IrisFolderPermissionsService),
  ) => ({
    fetchUserPermissions$: rxMethod<{ folderId: string; skipRequest: boolean }>(
      pipe(
        filter(({ folderId }) => !store.isFolderLoading(folderId)),
        mergeMap(({ folderId, skipRequest }) => {
          store.setFolderLoagingState(folderId, true);
          const request$ = skipRequest
            ? of(actionTypes)
            : folderPermissionsService.getPermissionActions(folderId);
          return request$.pipe(
            tapResponse({
              next: actions => {
                const permissions = store.mapActionsToPermissions(folderId, actions);
                patchState(store, setEntities(permissions, config));
              },
              error: () => store.setFolderLoagingState(folderId, false),
            }),
          );
        }),
      ),
    ),
  })),
  withMethods((
    store,
  ) => ({
    hasReadPermission: (folderId: string) => store.getUserPermissionsByFolderIdAndAction(folderId, FolderPermissionActionType.Read),
    hasUpdatePermission: (folderId: string) => store.getUserPermissionsByFolderIdAndAction(folderId, FolderPermissionActionType.Update),
    hasDeletePermission: (folderId: string) => store.getUserPermissionsByFolderIdAndAction(folderId, FolderPermissionActionType.Delete),
  })),
  withHooks({
    onInit: store => {
      const folderId = IrisDmsRecentFilesFolder.id;
      const permissions = store.mapActionsToPermissions(folderId, []);
      patchState(store, setEntities(permissions, config));
    },
  }),
);
